import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { FaChurch, FaCalendarCheck } from "react-icons/fa";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const formatoFecha = (fecha) =>
  fecha
    ? new Date(fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short", timeZone: "UTC" })
    : "";

export default function EstadisticasDominicalResumen() {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await fetch("/api/asistencia/estadisticas/servicios", {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Error obteniendo estadísticas dominicales");
        setServicios(await res.json());
      } catch (err) {
        console.error("Error cargando estadísticas dominicales:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, []);

  if (loading) {
    return <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-72 animate-pulse" />;
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <p className="text-sm text-slate-400">No se pudo cargar la asistencia de escuela dominical.</p>
      </div>
    );
  }

  const sinDatos = servicios.length === 0;
  // El API devuelve los servicios ordenados del más reciente al más antiguo.
  const ultimo = servicios[0] || null;
  const tendencia = [...servicios].slice(0, 6).reverse();

  const data = {
    labels: tendencia.map((s) => formatoFecha(s.fecha)),
    datasets: [
      {
        label: "Asistencia total",
        data: tendencia.map((s) => Number(s.total)),
        borderColor: "#4f46e5",
        backgroundColor: "rgba(79,70,229,0.12)",
        tension: 0.35,
        fill: true,
        pointRadius: 3,
        pointBackgroundColor: "#4f46e5",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#94a3b8" } },
      y: { grid: { color: "#f1f5f9" }, ticks: { color: "#94a3b8" }, beginAtZero: true },
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <FaChurch />
        </span>
        <h2 className="text-base font-semibold text-slate-800">Asistencia a Escuela Dominical</h2>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
            <FaCalendarCheck className="text-[10px]" /> Último servicio
          </p>
          <p className="text-base font-bold text-slate-800">{formatoFecha(ultimo?.fecha) || "—"}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 mb-1">Total asistencia</p>
          <p className="text-base font-bold text-slate-800">{ultimo?.total ?? 0}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 mb-1">Alumnos / Maestros</p>
          <p className="text-base font-bold text-slate-800">
            {ultimo?.alumnos ?? 0} / {ultimo?.maestros ?? 0}
          </p>
        </div>
      </div>

      {sinDatos ? (
        <p className="text-sm text-slate-400 text-center py-10">Aún no hay asistencia registrada.</p>
      ) : (
        <div className="h-40">
          <Line data={data} options={options} />
        </div>
      )}
    </div>
  );
}
